import { MeetingContext } from '@/meeting/contexts/MeetingContext'
import NextStepInlineForm from '@/nextStep/components/NextStepInlineForm'
import { NextStepFormsContext } from '@/nextStep/contexts/NextStepFormsContext'
import TaskInlineForm from '@/task/components/TaskInlineForm'
import { TaskFormsContext } from '@/task/contexts/TaskFormsContext'
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Box,
  StyleProps,
  VStack,
  forwardRef,
} from '@chakra-ui/react'
import {
  Thread_Activity_Type_Enum,
  useCreateThreadActivityMutation,
} from '@gql'
import { ThreadActivityMeetingNoteFragment } from '@rolebase/shared/model/thread_activity'
import useCurrentMember from '@/member/hooks/useCurrentMember'
import { ScrollableContext } from '@/common/hooks/useScrollable'
import { isSameDay } from 'date-fns'
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useTranslation } from 'react-i18next'
import { ThreadIcon } from 'src/icons'
import ProposalScrollButton from '@/proposal/components/ProposalScrollButton'
import { useLocation } from 'react-router'
import { ThreadContext } from '../contexts/ThreadContext'
import {
  getActivityIdFromHash,
  scrollAndHighlightActivity,
} from '../utils/scrollAndHighlightActivity'
import ThreadActivity from './ThreadActivity'
import { isResolutionOffered } from './threadResolutions'
import ThreadDaySeparator from './ThreadDaySeparator'

export const activityMeetingNoteTmpId = 'tmp'

export default forwardRef(function ThreadActivities(
  styleProps: StyleProps,
  ref
) {
  const { t } = useTranslation()
  const { thread, activities, memberStatus } = useContext(ThreadContext)!
  const meetingState = useContext(MeetingContext)
  const nextStepForms = useContext(NextStepFormsContext)
  const taskForms = useContext(TaskFormsContext)
  const currentMember = useCurrentMember()
  const [createThreadActivity] = useCreateThreadActivityMutation()
  const meetingCircle = meetingState?.isEnded ? undefined : meetingState?.circle

  // A validated step leaves the form and becomes the card above (G2-D26).
  // ⚠️ refMeetingId stays empty: thread_activity_active_unique would otherwise
  // allow only one step per tension and per meeting (G2-D7).
  const handleNextStepCreated = useCallback(
    async (nextStepId: string) => {
      if (!thread || !currentMember) return
      await createThreadActivity({
        variables: {
          values: {
            threadId: thread.id,
            memberId: currentMember.id,
            data: {},
            type: Thread_Activity_Type_Enum.NextStep,
            refNextStepId: nextStepId,
          },
        },
      })
    },
    [thread?.id, currentMember?.id, createThreadActivity]
  )
  // A project created from its card becomes the tension's "Project" activity,
  // exactly as when it is created from the window (#123).
  const handleTaskCreated = useCallback(
    async (taskId: string) => {
      if (!thread || !currentMember) return
      await createThreadActivity({
        variables: {
          values: {
            threadId: thread.id,
            memberId: currentMember.id,
            data: {},
            type: Thread_Activity_Type_Enum.Task,
            refTaskId: taskId,
          },
        },
      })
    },
    [thread?.id, currentMember?.id, createThreadActivity]
  )

  const scrollable = useContext(ScrollableContext)
  const { hash } = useLocation()

  // Scroll to the bottom on load, and when the last activity changes and was
  // created by the current member. Other members' new messages are left to the
  // scrollable layout (it keeps you at the bottom when already there).
  const loadedRef = useRef(false)
  const lastActivityIdRef = useRef<string | undefined>()

  useEffect(() => {
    if (!activities || !scrollable) return
    const lastActivity = activities[activities.length - 1]
    const lastId = lastActivity?.id

    if (!loadedRef.current) {
      loadedRef.current = true
      lastActivityIdRef.current = lastId

      // If the URL targets a specific activity (#activity-{id}), scroll to and
      // highlight it instead of pinning to the bottom. Deferred so the activity
      // is rendered before we scroll to it.
      const hashActivityId = getActivityIdFromHash(hash)
      if (hashActivityId && activities.some((a) => a.id === hashActivityId)) {
        setTimeout(() => scrollAndHighlightActivity(hashActivityId), 100)
        return
      }

      // Pin to bottom; the layout keeps following as async content grows
      scrollable.scrollToBottom(false)
      return
    }

    if (lastId && lastId !== lastActivityIdRef.current) {
      lastActivityIdRef.current = lastId
      if (lastActivity?.userId === currentMember?.userId) {
        scrollable.scrollToBottom(true)
      }
    }
  }, [activities])

  // Temporary meeting note
  const tmpMeetingNoteActivity = useMemo(() => {
    if (
      !thread ||
      !activities ||
      !meetingState?.meeting ||
      activities.some(
        (a) =>
          a.type === Thread_Activity_Type_Enum.MeetingNote &&
          a.refMeeting?.id === meetingState.meeting?.id
      )
    ) {
      return undefined
    }

    // Add temporary meeting note to activities
    return {
      id: activityMeetingNoteTmpId,
      threadId: thread.id,
      userId: '',
      // The meeting's own time, not "now": the note opens the session, so it
      // must sit before what the session produces. With new Date() it was
      // always the most recent activity and the next steps written in the
      // meeting appeared ABOVE the need they answer — the sequence read
      // backwards (Yohan, 10/08/2026).
      createdAt: meetingState.meeting.startDate,
      type: Thread_Activity_Type_Enum.MeetingNote,
      refMeeting: meetingState.meeting,
      reactions: [],
      member: null,
      data: {
        notes: '',
      },
    } as ThreadActivityMeetingNoteFragment
  }, [thread?.id, activities, meetingState?.meeting?.id])

  // The rendered list: real activities plus the temporary note, in one
  // chronological order. Appending the note after the list, as before, pinned
  // it to the bottom whatever its date.
  const renderedActivities = useMemo(() => {
    if (!activities) return activities
    if (!tmpMeetingNoteActivity) return activities
    return [...activities, tmpMeetingNoteActivity].sort((a, b) =>
      a.createdAt < b.createdAt ? -1 : a.createdAt > b.createdAt ? 1 : 0
    )
  }, [activities, tmpMeetingNoteActivity])

  // Show read mark on last read activity:
  // - at first load
  // - when it changes for an activity that is not the last one
  const [lastReadActivityId, setLastReadActivityId] = useState<
    string | undefined
  >()

  // Update read mark at first load
  useEffect(() => {
    if (!thread || !activities || thread.id !== activities[0]?.threadId) {
      return
    }
    const lastActivityId = activities[activities.length - 1]?.id
    const readActivityId = memberStatus?.lastReadActivityId

    if (readActivityId && readActivityId !== lastActivityId) {
      // Set mark to last read activity when opening thread
      setLastReadActivityId(readActivityId)
    } else {
      // Reset
      setLastReadActivityId(undefined)
    }
  }, [thread?.id, activities?.[0]?.threadId])

  // Update read mark when last read activity changes
  useEffect(() => {
    if (!memberStatus || !activities) return
    const readActivityId = memberStatus.lastReadActivityId
    if (!readActivityId) return
    const activityExists = activities.some((a) => a.id === readActivityId)
    const lastActivityId = activities[activities.length - 1]?.id
    if (activityExists && readActivityId !== lastActivityId) {
      setLastReadActivityId(readActivityId)
    }
  }, [memberStatus?.lastReadActivityId])

  // The next-step forms belong to the stream, right after the last activity:
  // a step is an element of the tension, like its meeting note (G2-D24). They
  // used to live in the composer and looked bolted onto the button bar.
  const offersNextStep =
    isResolutionOffered(Thread_Activity_Type_Enum.NextStep, thread?.type) &&
    !!meetingCircle

  // The form opens by itself only on a tension that has produced NOTHING yet
  // (Yohan, 11/08/2026). Once a step is written, an empty form underneath reads
  // as a demand for more rather than as an offer. Form 0 is the automatic one;
  // the ones the button adds keep their own keys and always show.
  const hasNextStep = activities?.some(
    (a) => a.type === Thread_Activity_Type_Enum.NextStep
  )
  const visibleNextStepForms = nextStepForms?.forms.filter(
    (key) => key !== 0 || !hasNextStep
  )

  return (
    <>
      <ProposalScrollButton />
      <VStack spacing={0} mb={2} align="stretch" ref={ref} {...styleProps}>
      {renderedActivities &&
        renderedActivities.map((activity, i) => (
          <React.Fragment key={`activity_${activity.id}`}>
            {(i === 0 ||
              !isSameDay(
                new Date(activity.createdAt),
                new Date(renderedActivities[i - 1].createdAt)
              )) && <ThreadDaySeparator date={activity.createdAt} />}

            <ThreadActivity activity={activity} />

            {lastReadActivityId === activity.id && (
              <Box h="3px" w="100%" bg="red.200" _dark={{ bg: 'red.800' }} />
            )}
          </React.Fragment>
        ))}

      {activities?.length === 0 &&
        !tmpMeetingNoteActivity &&
        !thread?.archivedAt && (
          <Alert
            status="success"
            variant="subtle"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            textAlign="center"
            height="200px"
          >
            <ThreadIcon size={40} />
            <AlertTitle mt={4} mb={1} fontSize="lg">
              {t('ThreadActivities.emptyTitle')}
            </AlertTitle>
            <AlertDescription maxWidth="sm">
              {t('ThreadActivities.emptyDescription')}
            </AlertDescription>
          </Alert>
        )}
      {offersNextStep &&
        meetingCircle &&
        thread &&
        visibleNextStepForms?.map((key) => (
          <NextStepInlineForm
            key={key}
            scopeCircleId={meetingCircle.id}
            meetingId={meetingState?.meeting?.id}
            threadId={thread.id}
            onCreated={async (nextStepId) => {
              await handleNextStepCreated(nextStepId)
              nextStepForms?.removeForm(key)
            }}
            onCancel={() => nextStepForms?.removeForm(key)}
          />
        ))}
      {meetingCircle &&
        thread &&
        isResolutionOffered(Thread_Activity_Type_Enum.Task, thread.type) &&
        taskForms?.forms.map((key) => (
          <TaskInlineForm
            key={key}
            onCreated={handleTaskCreated}
            onCancel={() => taskForms.removeForm(key)}
          />
        ))}
      </VStack>
    </>
  )
})
